import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query'
import { type PaginationOptions } from '../../../api/pagination'
import { fetchUsers, fetchSelfUser, fetchUser, fetchUserRoles, fetchCurrentOrganization, type UserFilters } from '../api/employeeApi'
import { type User, type Role } from '../../../utils/types'
import { type PaginatedResponse } from '../../../api/client'
import { type PaginatedQueryParameters, type DetailQueryParameters } from '../../../api/hooks/utils/types'
import { userKeys } from '../utils/queryKeys'

/**
 * Hook that provides the available User instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which instances to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters or instances changed.
 * 
 * Note: User type has string IDs which is incompatible with EntityBaseType,
 * so we use regular useQuery instead of useSmartListQuery.
 */
export function useUsers(
  parameters: PaginatedQueryParameters<User, UserFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true,
): UseQueryResult<PaginatedResponse<User>, Error> {
  return useQuery({
    queryKey: userKeys.list(parameters),
    queryFn: () => fetchUsers(parameters),
    enabled,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to fetch the current logged-in user
 */
export function useSelfUser(
  expand?: string[],
  enabled = true
): UseQueryResult<User, Error> {
  return useQuery({
    queryKey: userKeys.self(),
    queryFn: () => fetchSelfUser(expand),
    enabled,
  })
}

/**
 * Hook to fetch a single user
 * 
 * Note: User type has string IDs which is incompatible with EntityBaseType,
 * so we use regular useQuery instead of useSmartDetailQuery.
 */
export function useUser(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<User>,
  enabled = true,
): UseQueryResult<User, Error> {
  return useQuery({
    queryKey: userKeys.detail(idOrUrl, parameters),
    queryFn: () => fetchUser(idOrUrl, parameters.expand),
    enabled: enabled && Boolean(idOrUrl),
  })
}

/**
 * Hook to fetch user roles
 */
export function useUserRoles(
  userIdOrSelf: string | number,
  enabled = true
): UseQueryResult<Role[], Error> {
  return useQuery({
    queryKey: [...userKeys.detail(typeof userIdOrSelf === 'number' ? userIdOrSelf : Number(userIdOrSelf)), 'roles'],
    queryFn: () => fetchUserRoles(userIdOrSelf),
    enabled: enabled && Boolean(userIdOrSelf),
  })
}

/**
 * Hook to fetch current organization
 */
export function useCurrentOrganization(
  enabled = true
): UseQueryResult<string, Error> {
  return useQuery({
    queryKey: ['organization', 'current'],
    queryFn: fetchCurrentOrganization,
    enabled,
  })
}

// Alias for backward compatibility
export const useCurrentUser = useSelfUser

/**
 * Hook to fetch full user data from database for multiple user IDs from Elasticsearch
 * Similar to useArticlesFromSearch - loads complete user data after Elasticsearch search
 * 
 * Loads full user data from the database for each user ID
 * 
 * @param userIds - Array of user IDs from Elasticsearch search
 * @param expand - Fields to expand in the user response
 * @param enabled - Whether to enable the queries
 * @returns Object with users, loading states, and errors
 * 
 * @example
 * ```tsx
 * const { users, isLoading, hasErrors } = useUsersFromSearch(
 *   ['1', '2', '3'],
 *   ['roles__sub_roles']
 * )
 * ```
 */
export function useUsersFromSearch(
  userIds: string[],
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: userIds.map((id) => ({
      queryKey: userKeys.detail(id, { expand }),
      queryFn: () => fetchUser(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Extract users from query results
  const users = queries
    .filter((query) => query.isSuccess && query.data)
    .map((query) => query.data as User)

  // Check if any queries are still loading
  const isLoading = queries.some((query) => query.isLoading)

  // Check if all queries are done (either success or error)
  const isIdle = queries.every((query) => query.status === 'pending')
  const isDone = queries.every((query) => query.status !== 'pending')

  // Check for errors
  const hasErrors = queries.some((query) => query.isError)
  const errors = queries
    .filter((query) => query.isError)
    .map((query) => query.error)

  // Calculate progress
  const totalQueries = userIds.length
  const completedQueries = queries.filter(
    (query) => query.status !== 'pending'
  ).length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    users,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries, // Expose individual queries for advanced use cases
  }
}
